// YTMP3 Frontend Main Logic (Client-Side WASM Muxing & Encoding Workflow)

import { FFmpeg } from '@ffmpeg/ffmpeg';
import { toBlobURL, fetchFile } from '@ffmpeg/util';

const API_BASE = import.meta.env.VITE_API_BASE_URL || (import.meta.env.DEV ? 'http://localhost:8889' : 'https://api-dwn.rena.my.id');

document.addEventListener('DOMContentLoaded', () => {
  // DOM Elements
  const convertForm = document.getElementById('convertForm');
  const videoInput = document.getElementById('videoInput');
  const btnPaste = document.getElementById('btnPaste');
  const btnFetchInfo = document.getElementById('btnFetchInfo');

  const loaderCard = document.getElementById('loaderCard');
  const videoCard = document.getElementById('videoCard');
  const formatCard = document.getElementById('formatCard');

  const thumbImg = document.getElementById('thumbImg');
  const durationBadge = document.getElementById('durationBadge');
  const videoTitle = document.getElementById('videoTitle');
  const uploader = document.getElementById('uploader');
  const viewCount = document.getElementById('viewCount');

  const tabAudio = document.getElementById('tabAudio');
  const tabVideo = document.getElementById('tabVideo');
  const formatTableBody = document.getElementById('formatTableBody');

  const progressSection = document.getElementById('progressSection');
  const progressBar = document.getElementById('progressBar');
  const progressPercent = document.getElementById('progressPercent');
  const statusMessage = document.getElementById('statusMessage');

  const toastContainer = document.getElementById('toastContainer');

  let activeTab = 'audio'; // 'audio' or 'video'
  let currentVideoData = null;
  let ffmpegInstance = null;

  // Initialize FAQ Accordion
  initFaqAccordion();

  // Preload FFmpeg WASM Engine in background
  loadFFmpeg().catch((err) => console.warn('Background FFmpeg preload warning:', err));

  // Paste Button Listener
  if (btnPaste) {
    btnPaste.addEventListener('click', async () => {
      try {
        if (navigator.clipboard && navigator.clipboard.readText) {
          const text = await navigator.clipboard.readText();
          if (text) {
            videoInput.value = text.trim();
            showToast('URL pasted from clipboard!', 'info');
          }
        } else {
          showToast('Clipboard access unavailable. Please paste manually.', 'error');
        }
      } catch (err) {
        showToast('Failed to paste from clipboard.', 'error');
      }
    });
  }

  // Convert Form Submit
  if (convertForm) {
    convertForm.addEventListener('submit', (e) => {
      e.preventDefault();
      handleFetchVideoInfo();
    });
  }

  // Tab Listeners
  if (tabAudio) {
    tabAudio.addEventListener('click', () => {
      if (activeTab !== 'audio') {
        activeTab = 'audio';
        tabAudio.classList.add('active');
        tabVideo.classList.remove('active');
        renderFormatsTable();
      }
    });
  }

  if (tabVideo) {
    tabVideo.addEventListener('click', () => {
      if (activeTab !== 'video') {
        activeTab = 'video';
        tabVideo.classList.add('active');
        tabAudio.classList.remove('active');
        renderFormatsTable();
      }
    });
  }

  // FFmpeg WASM Engine Loader
  async function loadFFmpeg() {
    if (ffmpegInstance) return ffmpegInstance;

    logStatus('Initializing FFmpeg.wasm engine in browser...');
    const ffmpeg = new FFmpeg();

    ffmpeg.on('log', ({ message }) => {
      console.log('[FFmpeg.wasm]', message);
    });

    ffmpeg.on('progress', ({ progress }) => {
      const percent = Math.min(Math.round(progress * 100), 100);
      setProgress(50 + Math.round(percent / 2));
      logStatus(`Merging / Converting streams in browser FFmpeg.wasm: ${percent}%...`);
    });

    const baseURL = 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.10/dist/esm';

    try {
      logStatus('Loading FFmpeg.wasm JS wrapper...');
      const coreURL = await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript');

      logStatus('Downloading FFmpeg.wasm core binary (31 MB from CDN)...');
      const wasmURL = await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm');

      logStatus('Compiling WebAssembly engine module...');
      await ffmpeg.load({
        coreURL: coreURL,
        wasmURL: wasmURL,
      });

      ffmpegInstance = ffmpeg;
      logStatus('FFmpeg.wasm engine initialized and ready!');
      return ffmpegInstance;
    } catch (err) {
      const msg = `FFmpeg.wasm Load Error: ${err.message || err}`;
      logStatus(msg);
      console.error(err);
      throw err;
    }
  }

  // Progress UI Helpers
  function setProgress(percent) {
    if (progressBar) progressBar.style.width = `${percent}%`;
    if (progressPercent) progressPercent.textContent = `${percent}%`;
  }

  function logStatus(message) {
    if (statusMessage) {
      statusMessage.innerHTML = `<i class="fa-solid fa-spinner fa-spin"></i> ${message}`;
    }
    console.log('[Status]', message);
  }

  // Safe WASM Virtual File Delete Helper
  async function safeDeleteFile(ffmpeg, fileName) {
    try {
      await ffmpeg.deleteFile(fileName);
    } catch (e) {
      // Ignore if file doesn't exist
    }
  }

  // Fetch Stream Blob with Progress
  async function fetchBlobWithProgress(url, label, startPct, endPct) {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${label}: HTTP ${response.status}`);
    }

    const contentLength = response.headers.get('content-length');
    const totalBytes = contentLength ? parseInt(contentLength, 10) : 0;
    const reader = response.body.getReader();

    let receivedBytes = 0;
    const chunks = [];

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      chunks.push(value);
      receivedBytes += value.length;

      const recMB = (receivedBytes / (1024 * 1024)).toFixed(1);
      if (totalBytes > 0) {
        const totalMB = (totalBytes / (1024 * 1024)).toFixed(1);
        const ratio = receivedBytes / totalBytes;
        const currentPct = startPct + Math.round(ratio * (endPct - startPct));
        setProgress(currentPct);
        logStatus(`${label}: ${recMB} MB / ${totalMB} MB (${Math.round(ratio * 100)}%)`);
      } else {
        logStatus(`${label}: ${recMB} MB downloaded...`);
      }
    }

    if (receivedBytes === 0) {
      throw new Error(`${label} response is empty (0 bytes received from server). Check backend connection.`);
    }

    return new Blob(chunks);
  }

  // Format Helper
  function formatBytesToMB(bytes) {
    if (!bytes || bytes <= 0) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  }

  // Helper to extract YouTube Video ID
  function extractYouTubeId(url) {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|shorts\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) {
      return match[2];
    }
    if (url.length === 11) return url;
    return null;
  }

  // Handle Video Info Fetching from Backend API
  async function handleFetchVideoInfo() {
    const rawInput = videoInput.value.trim();
    if (!rawInput) {
      showToast('Please enter a valid YouTube Video ID or URL', 'error');
      return;
    }

    const videoId = extractYouTubeId(rawInput) || rawInput;

    btnFetchInfo.disabled = true;
    btnFetchInfo.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Fetching...';
    loaderCard.classList.remove('hidden');
    videoCard.classList.add('hidden');
    formatCard.classList.add('hidden');
    progressSection.classList.add('hidden');

    try {
      const response = await fetch(`${API_BASE}/api/v1/youtube/info?id=${encodeURIComponent(videoId)}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch video info from backend`);
      }

      const resData = await response.json();
      const data = resData.data || resData;

      currentVideoData = data;

      // Update Metadata Card UI
      thumbImg.src = data.thumbnail || `https://i.ytimg.com/vi/${data.id || videoId}/hqdefault.jpg`;
      durationBadge.textContent = data.duration_formatted || `${data.duration || 0}s`;
      videoTitle.textContent = data.title || 'Untitled Video';
      uploader.textContent = data.uploader || 'Unknown Channel';
      viewCount.textContent = `${(data.view_count || 0).toLocaleString()} views`;

      videoCard.classList.remove('hidden');
      formatCard.classList.remove('hidden');

      renderFormatsTable();
      showToast('Video details loaded successfully!', 'success');
    } catch (err) {
      showToast(`Error fetching info: ${err.message}`, 'error');
      console.error('Fetch info error:', err);
    } finally {
      btnFetchInfo.disabled = false;
      btnFetchInfo.innerHTML = '<i class="fa-solid fa-magnifying-glass"></i> <span>Convert</span>';
      loaderCard.classList.add('hidden');
    }
  }

  // Render Formats Table
  function renderFormatsTable() {
    if (!currentVideoData) return;
    formatTableBody.innerHTML = '';

    if (activeTab === 'video') {
      let videoFormats = Array.isArray(currentVideoData.video_formats) ? [...currentVideoData.video_formats] : [];

      if (videoFormats.length === 0 && Array.isArray(currentVideoData.formats)) {
        videoFormats = currentVideoData.formats.filter((f) => f.vcodec && f.vcodec !== 'none');
      }
      if (videoFormats.length === 0 && Array.isArray(currentVideoData.muxed_formats)) {
        videoFormats = [...currentVideoData.muxed_formats];
      }

      videoFormats.sort((a, b) => (b.height || 0) - (a.height || 0));

      const seenHeights = new Set();
      const uniqueFormats = [];
      for (const f of videoFormats) {
        const key = `${f.height || f.quality_label || f.format_id}_${f.ext || 'mp4'}`;
        if (!seenHeights.has(key)) {
          seenHeights.add(key);
          uniqueFormats.push(f);
        }
      }

      uniqueFormats.forEach((f) => {
        const tr = document.createElement('tr');

        let rawQuality = f.quality_label || (f.height > 0 ? `${f.height}p${f.fps > 30 ? f.fps : ''}` : 'HD');
        const qualityText = rawQuality.split(',')[0].split(';')[0].trim();
        const isHigh = f.height >= 1080 || qualityText.includes('1080');
        const qualityClass = isHigh ? 'quality-tag high' : 'quality-tag normal';
        const badgeIcon = isHigh ? '<i class="fa-solid fa-crown"></i>' : '<i class="fa-solid fa-check"></i>';

        const formatText = (f.ext || f.container || 'mp4').toUpperCase();
        const sizeText = formatBytesToMB(f.filesize || f.filesize_approx);

        tr.innerHTML = `
          <td><span class="${qualityClass}">${badgeIcon} ${qualityText}</span></td>
          <td><strong>${formatText}</strong></td>
          <td>${sizeText}</td>
          <td class="text-right">
            <button class="btn-primary btn-convert btn-video-download" data-format-id="${f.format_id}" data-ext="${f.ext || 'mp4'}">
              <i class="fa-solid fa-download"></i> Download
            </button>
          </td>
        `;

        formatTableBody.appendChild(tr);
      });

      formatTableBody.querySelectorAll('.btn-video-download').forEach((btn) => {
        btn.addEventListener('click', () => {
          const formatId = btn.getAttribute('data-format-id');
          const ext = btn.getAttribute('data-ext');
          startFormatDownload(formatId, ext, btn);
        });
      });
    } else {
      // Audio (MP3) Tab: 320 kbps, 256 kbps, 192 kbps, 128 kbps
      const durationSec = currentVideoData.duration || 240;
      const bitrates = [
        { kbps: 320, label: '320 kbps (Best Quality)', isHigh: true },
        { kbps: 256, label: '256 kbps (High)', isHigh: false },
        { kbps: 192, label: '192 kbps (Medium)', isHigh: false },
        { kbps: 128, label: '128 kbps (Standard)', isHigh: false },
      ];

      bitrates.forEach((item) => {
        const estimatedBytes = Math.round(((item.kbps * 1000) / 8) * durationSec);
        const sizeText = formatBytesToMB(estimatedBytes);

        const qualityClass = item.isHigh ? 'quality-tag high' : 'quality-tag normal';
        const badgeIcon = item.isHigh ? '<i class="fa-solid fa-crown"></i>' : '<i class="fa-solid fa-check"></i>';

        const tr = document.createElement('tr');
        tr.innerHTML = `
          <td><span class="${qualityClass}">${badgeIcon} ${item.label}</span></td>
          <td><strong>MP3</strong></td>
          <td>${sizeText}</td>
          <td class="text-right">
            <button class="btn-primary btn-convert btn-audio-convert" data-bitrate="${item.kbps}">
              <i class="fa-solid fa-bolt"></i> Convert
            </button>
          </td>
        `;
        formatTableBody.appendChild(tr);
      });

      formatTableBody.querySelectorAll('.btn-audio-convert').forEach((btn) => {
        btn.addEventListener('click', () => {
          const kbps = parseInt(btn.getAttribute('data-bitrate'), 10);
          startAudioMP3Conversion(kbps, btn);
        });
      });
    }
  }

  // Client-Side Video Muxing (FFmpeg.wasm)
  async function startFormatDownload(videoFormatId, videoExt, btnElement) {
    if (!currentVideoData) return;

    const allBtns = document.querySelectorAll('.btn-convert');
    allBtns.forEach((b) => (b.disabled = true));
    if (btnElement) {
      btnElement.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Processing...';
    }

    progressSection.classList.remove('hidden');
    setProgress(5);

    try {
      logStatus('Preparing & initializing FFmpeg.wasm engine...');
      const ffmpeg = await loadFFmpeg();

      const videoStreamUrl = `${API_BASE}/api/v1/youtube/stream?id=${encodeURIComponent(currentVideoData.id)}&format_id=${encodeURIComponent(videoFormatId)}`;
      const audioStreamUrl = `${API_BASE}/api/v1/youtube/stream?id=${encodeURIComponent(currentVideoData.id)}&format_id=bestaudio/ba`;

      logStatus('Connecting to backend & downloading Video Stream...');
      const videoBlob = await fetchBlobWithProgress(videoStreamUrl, 'Downloading Video Stream', 10, 35);

      logStatus('Connecting to backend & downloading Best Quality Audio Stream...');
      const audioBlob = await fetchBlobWithProgress(audioStreamUrl, 'Downloading Audio Stream', 35, 50);

      const isWebm = (videoExt || '').toLowerCase() === 'webm';
      const inVideo = `in_v.${isWebm ? 'webm' : 'mp4'}`;
      const inAudio = `in_a.m4a`;
      const outFile = isWebm ? 'out.mkv' : 'out.mp4';
      const mimeType = isWebm ? 'video/x-matroska' : 'video/mp4';
      const outExt = isWebm ? 'mkv' : 'mp4';

      await safeDeleteFile(ffmpeg, inVideo);
      await safeDeleteFile(ffmpeg, inAudio);
      await safeDeleteFile(ffmpeg, outFile);

      logStatus('Writing streams to WebAssembly Virtual Filesystem...');
      setProgress(52);
      await ffmpeg.writeFile(inVideo, await fetchFile(videoBlob));
      await ffmpeg.writeFile(inAudio, await fetchFile(audioBlob));

      logStatus('Merging video with best audio quality in browser WASM (-c copy)...');
      setProgress(55);
      await ffmpeg.exec(['-y', '-i', inVideo, '-i', inAudio, '-c:v', 'copy', '-c:a', 'copy', outFile]);

      logStatus('Reading merged output file from WASM memory...');
      setProgress(95);
      const data = await ffmpeg.readFile(outFile);
      const outputBlob = new Blob([data.buffer], { type: mimeType });

      await safeDeleteFile(ffmpeg, inVideo);
      await safeDeleteFile(ffmpeg, inAudio);
      await safeDeleteFile(ffmpeg, outFile);

      setProgress(100);
      logStatus('Muxing complete! Triggering file download...');
      showToast('Video download complete!', 'success');

      const a = document.createElement('a');
      a.href = URL.createObjectURL(outputBlob);
      a.download = `${currentVideoData.title || 'video'}_${videoFormatId}.${outExt}`;
      a.click();

      logStatus('SUCCESS: File downloaded successfully to client!');
    } catch (err) {
      const errMsg = err.message || String(err);
      logStatus(`Error during download: ${errMsg}`);
      console.error('Download error details:', err);
      showToast(`Download failed: ${errMsg}`, 'error');
    } finally {
      allBtns.forEach((b) => (b.disabled = false));
      if (btnElement) {
        btnElement.innerHTML = '<i class="fa-solid fa-download"></i> Download';
      }
    }
  }

  // Client-Side MP3 Conversion (FFmpeg.wasm)
  async function startAudioMP3Conversion(kbps, btnElement) {
    if (!currentVideoData) return;

    const allBtns = document.querySelectorAll('.btn-convert');
    allBtns.forEach((b) => (b.disabled = true));
    if (btnElement) {
      btnElement.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Converting...';
    }

    progressSection.classList.remove('hidden');
    setProgress(5);

    try {
      logStatus('Preparing & initializing FFmpeg.wasm engine...');
      const ffmpeg = await loadFFmpeg();

      const audioStreamUrl = `${API_BASE}/api/v1/youtube/stream?id=${encodeURIComponent(currentVideoData.id)}&format_id=bestaudio/ba`;

      logStatus('Connecting to backend & downloading Best Quality Audio Stream...');
      const audioBlob = await fetchBlobWithProgress(audioStreamUrl, 'Downloading Audio Stream', 10, 45);

      const inAudio = `in_a.m4a`;
      const outFile = `out.mp3`;

      await safeDeleteFile(ffmpeg, inAudio);
      await safeDeleteFile(ffmpeg, outFile);
      await safeDeleteFile(ffmpeg, 'thumb.jpg');

      logStatus('Writing audio stream to WebAssembly Virtual Filesystem...');
      setProgress(45);
      await ffmpeg.writeFile(inAudio, await fetchFile(audioBlob));

      // Fetch Thumbnail for Album Cover Art
      let hasThumb = false;
      if (currentVideoData.thumbnail) {
        try {
          logStatus('Downloading video thumbnail for MP3 album cover art...');
          const thumbBlob = await fetchBlobWithProgress(currentVideoData.thumbnail, 'Thumbnail', 45, 50);
          if (thumbBlob && thumbBlob.size > 0) {
            await ffmpeg.writeFile('thumb.jpg', await fetchFile(thumbBlob));
            hasThumb = true;
          }
        } catch (e) {
          console.warn('Could not fetch thumbnail for MP3 cover art:', e);
        }
      }

      logStatus(`Converting audio to MP3 (${kbps} kbps) with embedded cover art & ID3 tags...`);
      setProgress(55);

      const ffmpegArgs = ['-y', '-i', inAudio];
      if (hasThumb) {
        ffmpegArgs.push('-i', 'thumb.jpg');
        ffmpegArgs.push('-map', '0:a', '-map', '1:0');
        ffmpegArgs.push('-c:v', 'copy');
        ffmpegArgs.push('-metadata:s:v', 'title="Album cover"');
        ffmpegArgs.push('-metadata:s:v', 'comment="Cover (front)"');
        ffmpegArgs.push('-disposition:v', 'attached_pic');
      }
      ffmpegArgs.push('-b:a', `${kbps}k`);
      if (currentVideoData.title) {
        ffmpegArgs.push('-metadata', `title=${currentVideoData.title}`);
      }
      if (currentVideoData.uploader) {
        ffmpegArgs.push('-metadata', `artist=${currentVideoData.uploader}`);
      }
      ffmpegArgs.push('-id3v2_version', '3');
      ffmpegArgs.push(outFile);

      await ffmpeg.exec(ffmpegArgs);

      logStatus('Reading MP3 output file from WASM memory...');
      setProgress(95);
      const data = await ffmpeg.readFile(outFile);
      const outputBlob = new Blob([data.buffer], { type: 'audio/mp3' });

      await safeDeleteFile(ffmpeg, inAudio);
      await safeDeleteFile(ffmpeg, outFile);
      if (hasThumb) await safeDeleteFile(ffmpeg, 'thumb.jpg');

      setProgress(100);
      logStatus('Conversion complete! Triggering MP3 file download...');
      showToast(`MP3 ${kbps}kbps conversion complete!`, 'success');

      const a = document.createElement('a');
      a.href = URL.createObjectURL(outputBlob);
      a.download = `${currentVideoData.title || 'audio'}_${kbps}kbps.mp3`;
      a.click();

      logStatus(`SUCCESS: MP3 (${kbps} kbps) downloaded successfully!`);
    } catch (err) {
      const errMsg = err.message || String(err);
      logStatus(`Error during MP3 conversion: ${errMsg}`);
      console.error('MP3 conversion error details:', err);
      showToast(`MP3 conversion failed: ${errMsg}`, 'error');
    } finally {
      allBtns.forEach((b) => (b.disabled = false));
      if (btnElement) {
        btnElement.innerHTML = '<i class="fa-solid fa-bolt"></i> Convert';
      }
    }
  }

  // Accordion Logic for FAQ
  function initFaqAccordion() {
    const faqItems = document.querySelectorAll('.faq-item');
    faqItems.forEach((item) => {
      const btn = item.querySelector('.faq-question');
      if (btn) {
        btn.addEventListener('click', () => {
          const isActive = item.classList.contains('active');

          faqItems.forEach((i) => {
            i.classList.remove('active');
            const questionBtn = i.querySelector('.faq-question');
            if (questionBtn) questionBtn.setAttribute('aria-expanded', 'false');
          });

          if (!isActive) {
            item.classList.add('active');
            btn.setAttribute('aria-expanded', 'true');
          }
        });
      }
    });
  }

  // Toast Notification System
  function showToast(message, type = 'info') {
    if (!toastContainer) return;
    const toast = document.createElement('div');
    toast.className = `toast ${type}`;

    let icon = '<i class="fa-solid fa-info-circle"></i>';
    if (type === 'success') icon = '<i class="fa-solid fa-circle-check"></i>';
    if (type === 'error') icon = '<i class="fa-solid fa-triangle-exclamation"></i>';

    toast.innerHTML = `${icon} <span>${message}</span>`;
    toastContainer.appendChild(toast);

    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateX(100%)';
      toast.style.transition = 'all 0.3s ease';
      setTimeout(() => toast.remove(), 300);
    }, 3500);
  }
});
